import { api, toast, inspectorForm, modalConfirm } from '../state.js';
import { esc, badge, formatDateTime } from '/core/js/utils.js';

/**
 * Which networks can reach this console (chunk 4.8c). Super Admin only.
 *
 * The gate (\system\access\IpAllowlist) covers the console and its sign-in;
 * account settings is never restricted. With the switch on, only enabled
 * networks get in — and an empty list restricts nothing. Every change that
 * would shut out the address you are using now is refused by the API (409)
 * and offered again here as "… anyway", with the way back in spelled out.
 */
class ArcAccess extends HTMLElement {
  #data = null;

  connectedCallback() {
    this.innerHTML = `
      <div class="page-header">
        <div class="detail-title"><h1>Access</h1></div>
      </div>
      <p class="text-muted">Limit which networks can reach this console, sign-in included.
        Account settings is never restricted: anyone can reach it to manage their own mail and password.</p>
      <div id="ac-body"><div class="loading-center"><div class="spinner"></div></div></div>`;

    // One set of listeners on the persistent body; its contents are redrawn.
    const body = this.querySelector('#ac-body');
    body.addEventListener('change', e => {
      if (e.target.matches('#ac-switch')) this.#setRestriction(e.target);
    });
    body.addEventListener('click', e => {
      const btn = e.target.closest('button[data-act]');
      if (!btn) return;
      const entry = this.#data.entries.find(x => x.id === Number(btn.dataset.id));
      ({
        add:    () => this.#add(),
        mine:   () => this.#add(this.#data.your_ip, 'My address'),
        toggle: () => this.#toggle(entry),
        remove: () => this.#remove(entry),
      })[btn.dataset.act]?.();
    });

    this.#load();
  }

  async #load() {
    try {
      this.#data = await api.access.ipStatus();
      this.#render();
    } catch (err) {
      this.querySelector('#ac-body').innerHTML = err.status === 403
        ? '<div class="empty-state"><h3>Super Admin only</h3><p>Only a Super Admin can change who can reach this console.</p></div>'
        : `<div class="empty-state"><h3>Could not load the allowlist</h3><p>${esc(err.message)}</p></div>`;
    }
  }

  #render() {
    const d = this.#data;
    const live = d.entries.filter(e => e.enabled);
    const state = !d.enabled
      ? 'Off — this console can be reached from anywhere. It still needs a sign-in.'
      : live.length
        ? 'On — only the enabled networks below can reach this console, sign-in included.'
        : 'On, but no network is enabled, so nothing is restricted yet.';

    this.querySelector('#ac-body').innerHTML = `
      <div class="card">
        <div class="card-header"><span>Restriction</span></div>
        <div class="card-body">
          <div class="toggle-row">
            <div>
              <strong>Only allow the networks below</strong>
              <span class="toggle-note" data-ac="state">${esc(state)}</span>
            </div>
            <label class="switch"><input type="checkbox" id="ac-switch" ${d.enabled ? 'checked' : ''}>
              <span class="slider"></span></label>
          </div>
          <p class="card-block">Your address now is <code>${esc(d.your_ip)}</code>
            ${d.covered ? badge('active', 'allowed') : badge('warning', 'not on the list')}
            ${d.covered ? '' : '<button type="button" class="btn btn-sm btn-secondary" data-act="mine">Add my address</button>'}</p>
        </div>
      </div>

      <div class="card">
        <div class="card-header"><span>Allowed networks</span>
          <button type="button" class="btn btn-sm btn-primary" data-act="add">Add network</button></div>
        ${d.entries.length ? `
          <div class="table-wrapper">
            <table>
              <thead><tr><th scope="col">Network</th><th scope="col">Label</th><th scope="col">Status</th><th scope="col">Added</th><th scope="col"></th></tr></thead>
              <tbody>${d.entries.map(e => `
                <tr data-entry="${e.id}">
                  <td class="mono">${esc(e.cidr)}</td>
                  <td>${esc(e.label ?? '')}</td>
                  <td>${badge(e.enabled ? 'enabled' : 'none', e.enabled ? 'enabled' : 'disabled')}</td>
                  <td class="mono nowrap">${esc(formatDateTime(e.created_at))}</td>
                  <td class="text-right">
                    <button type="button" class="btn btn-sm btn-ghost" data-act="toggle" data-id="${e.id}">${e.enabled ? 'Disable' : 'Enable'}</button>
                    <button type="button" class="btn btn-sm btn-danger-ghost" data-act="remove" data-id="${e.id}">Remove</button>
                  </td>
                </tr>`).join('')}</tbody>
            </table>
          </div>`
        : '<div class="card-body"><p class="text-muted">No networks yet. Add the ones you work from before switching the restriction on.</p></div>'}
      </div>

      <div class="card">
        <div class="card-header"><span>If you are locked out</span></div>
        <div class="card-body">
          <p class="text-muted">These addresses always get in, whatever the list says (<code>arc.admin_ip_break_glass</code>
            in <code>config.php</code>): ${d.break_glass.length ? d.break_glass.map(ip => `<code>${esc(ip)}</code>`).join(', ') : 'none'}.
            On the server, <code>php bin/access.php off</code> switches the restriction off and keeps the list.</p>
        </div>
      </div>`;
  }

  /**
   * Run a change; if the API says it would shut out the address in use (409),
   * ask, and send it again with force. Returns whether it went through.
   */
  async #withLockoutCheck(send, what) {
    try {
      await send(false);
      return true;
    } catch (err) {
      if (err.status !== 409) throw err;
      const ok = await modalConfirm(err.message, {
        title: 'You would lose access', confirmText: `${what} anyway`,
        details: 'From this address you would no longer reach this console. To get back in: sign in from a '
          + 'network on the list, or on the server run php bin/access.php off.',
      });
      if (!ok) return false;
      await send(true);
      return true;
    }
  }

  async #setRestriction(input) {
    const on = input.checked;
    input.disabled = true;
    try {
      const done = await this.#withLockoutCheck(force => api.access.ipSetEnabled(on, force), 'Switch on');
      if (done) toast(on ? 'Restriction switched on' : 'Restriction switched off', 'success');
    } catch (err) {
      toast(err.message ?? 'Could not change it', 'error');
    }
    this.#load();
  }

  async #toggle(entry) {
    const on = !entry.enabled;
    try {
      const done = await this.#withLockoutCheck(force => api.access.ipToggle(entry.id, on, force), 'Disable');
      if (done) toast(`${entry.cidr} ${on ? 'enabled' : 'disabled'}`, 'success');
    } catch (err) {
      toast(err.message ?? 'Could not change it', 'error');
    }
    this.#load();
  }

  async #remove(entry) {
    const ok = await modalConfirm(`Remove ${entry.cidr} from the list?`, {
      title: 'Remove network', confirmText: 'Remove',
    });
    if (!ok) return;
    try {
      const done = await this.#withLockoutCheck(force => api.access.ipRemove(entry.id, force), 'Remove');
      if (done) toast(`${entry.cidr} removed`, 'success');
    } catch (err) {
      toast(err.message ?? 'Could not remove it', 'error');
    }
    this.#load();
  }

  #add(cidr = '', label = '') {
    inspectorForm({
      title: 'Add network',
      width: 420,
      submitLabel: 'Add',
      body: `
        <label class="form-field"><span>Address or range *</span>
          <input name="cidr" class="mono" required autocomplete="off" value="${esc(cidr)}"
                 placeholder="203.0.113.7 or 203.0.113.0/24"></label>
        <label class="form-field"><span>Label</span>
          <input name="label" autocomplete="off" maxlength="120" value="${esc(label)}" placeholder="Office"></label>
        <p class="field-hint">It is enabled as soon as it is added.</p>`,
      onSubmit: async (f) => {
        const res = await api.access.ipAdd(f.cidr.value.trim(), f.label.value.trim() || undefined);
        toast(`${res.cidr} added`, 'success');
        this.#load();
      },
    });
  }
}

customElements.define('arc-access', ArcAccess);
